import type React from "react";
import { Card, CardContent, Typography, Box, styled, Chip } from "@mui/material";
import { alpha } from "@mui/system";
import { DateTime } from "luxon";
import { useIntl } from "react-intl";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import type { TenantMeta, TenantStatusName } from "./tenant-card";
import { TenantStatusChip } from "./tenant-status-chip";

const StyledCard = styled(Card, { shouldForwardProp: (prop) => prop !== "active" })<{ active: boolean }>(
	({ active, theme }) => ({
		position: "relative",
		cursor: "pointer",
		flexShrink: 0,
		height: "100%",
		backgroundColor: theme.palette.common.white,
		border: active ? `1px solid ${theme.palette.success.main} !important` : undefined,
		"&:hover": {
			backgroundColor: alpha(theme.palette.primary.main, 0.2),
			border: active ? `1px solid ${theme.palette.success.main} !important` : undefined,
			borderColor: active ? theme.palette.success.main : alpha(theme.palette.primary.main, 0.3),
		},
	}),
);

export interface TenantCardTemplateProps {
	readonly tenant: TenantMeta;
	readonly isActiveTenant: boolean;
	readonly tenantStatus: TenantStatusName;
	readonly onClick: (e: React.MouseEvent) => void;
	readonly tenantcardActions?: React.ReactNode;
}

export const TenantCardTemplate: React.FC<TenantCardTemplateProps> = ({
	tenant,
	isActiveTenant,
	tenantStatus,
	onClick,
	tenantcardActions,
}) => {
	const intl = useIntl();

	const getExpirationText = () => {
		if (tenant.expiresAt === "" || tenant.expiresAt === null) {
			return null;
		}

		const expiresAt = DateTime.fromISO(tenant.expiresAt);
		const now = DateTime.now();
		const diff = expiresAt.diff(now, ["days", "hours", "minutes"]);

		if (diff.days >= 1) {
			return intl.formatMessage(
				{
					id: "Access expires in {days} days",
					defaultMessage: "Access expires in {days} days",
				},
				{ days: Math.floor(diff.days) },
			);
		}

		if (diff.hours >= 1) {
			return intl.formatMessage(
				{
					id: "Access expires in {hours} hours",
					defaultMessage: "Access expires in {hours} hours",
				},
				{ hours: Math.floor(diff.hours) },
			);
		}

		if (diff.minutes >= 1) {
			return intl.formatMessage(
				{
					id: "Access expires in {minutes} minutes",
					defaultMessage: "Access expires in {minutes} minutes",
				},
				{ minutes: Math.floor(diff.minutes) },
			);
		}

		return intl.formatMessage({
			id: "Access expired",
			defaultMessage: "Access expired",
		});
	};

	const expirationText = getExpirationText();
	const siteChip = isActiveTenant ? (
		<Chip label={intl.formatMessage({ id: "Current site", defaultMessage: "Current site" })} size="small" />
	) : null;

	return (
		<StyledCard active={isActiveTenant} onClick={onClick}>
			<CardContent sx={{ padding: 2, "&:last-child": { paddingBottom: 3 } }}>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Box display="flex" gap={1} flexDirection="column" flex={1} minWidth={0}>
						<Box display="flex" justifyContent="space-between">
							<Typography variant="h4">{tenant.company}</Typography>
							<Box sx={{ display: "flex", alignItems: "center" }}>{tenantcardActions}</Box>
						</Box>
						<Box display="flex" flexDirection="column">
							<Box gap={1} display="flex" alignItems="center" flexWrap="wrap">
								{siteChip}

								<TenantStatusChip tenantStatus={tenantStatus} />

								{expirationText !== null && (
									<Box display="flex" gap={0.2} alignItems="center" flexWrap="wrap">
										<AccessTimeIcon sx={{ fontSize: "12px" }} />
										<Typography variant="subtitle2">{expirationText}</Typography>
									</Box>
								)}
							</Box>
						</Box>
					</Box>
				</Box>
			</CardContent>
		</StyledCard>
	);
};
